import { Badge, IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, ListSubheader } from "@material-ui/core";
import { AccountBalance, ArrowBack, ArrowForward, AttachMoney, FormatListBulleted, MoneyOff, Person, TableChart } from "@material-ui/icons";
import React from "react";
import { Link, Route, Switch as RouterSwitch, useHistory } from "react-router-dom";

import { PageNavigation } from "components/PageNavigation";
import { ContactInfo } from "models/ContactInfo";
import { IdMap } from "models/IdMap";
import { Nullable } from "models/Nullable";
import { OfferRoom } from "models/OfferRoom";

import { BasicSummaryTable } from "./BasicSummaryTable";
import { DetailedSummaryTable } from "./DetailedSummaryTable";
import { InternalOfferAccounting } from "./InternalOfferAccounting";
import { EditableContactInfo, OfferContactInfo } from "./OfferContactInfo";
import { offerNavigation } from "./OfferRoomCreationScreen";

type OfferSummaryOverviewLinksProps = {
    path: string
    contactInfoBadge?: number
}

export function OfferSummaryOverviewLinks({ path, contactInfoBadge }: OfferSummaryOverviewLinksProps) {
    return (
        <List subheader={<ListSubheader>Oppsummering</ListSubheader>}>
            <ListItem divider button component={Link} to={path + "/enkel-oppsummering"}>
                <ListItemIcon>
                    <FormatListBulleted />
                </ListItemIcon>
                <ListItemText primary="Enkel" />
                <ListItemSecondaryAction>
                    <IconButton component={Link} to={path + "/enkel-oppsummering"}>
                        <ArrowForward />
                    </IconButton>
                </ListItemSecondaryAction>
            </ListItem>
            <ListItem divider button component={Link} to={path + "/detaljert-oppsummering"}>
                <ListItemIcon>
                    <TableChart />
                </ListItemIcon>
                <ListItemText primary="Detaljert" />
                <ListItemSecondaryAction>
                    <IconButton component={Link} to={path + "/detaljert-oppsummering"}>
                        <ArrowForward />
                    </IconButton>
                </ListItemSecondaryAction>
            </ListItem>
            <ListItem divider button component={Link} to={path + "/internt-regnskap"}>
                <ListItemIcon>
                    <AccountBalance />
                </ListItemIcon>
                <ListItemText primary="Internt Regnskap" />
                <ListItemSecondaryAction>
                    <IconButton component={Link} to={path + "/internt-regnskap"}>
                        <ArrowForward />
                    </IconButton>
                </ListItemSecondaryAction>
            </ListItem>
            <ListItem divider button component={Link} to={path + "/kontaktinfo"}>
                <ListItemIcon>
                    <Badge badgeContent={contactInfoBadge} color="secondary">
                        <Person />
                    </Badge>
                </ListItemIcon>
                <ListItemText primary="Kontaktinfo" />
                <ListItemSecondaryAction>
                    <IconButton component={Link} to={path + "/kontaktinfo"}>
                        <ArrowForward />
                    </IconButton>
                </ListItemSecondaryAction>
            </ListItem>
        </List>
    )
}

type Props = {
    offerId: string
    path: string
    offerRooms: IdMap<OfferRoom>
    offerRoomComments: { [offerRoomId: string]: string }
    offerRoomNames: { [offerRoomId: string]: string }
    setOfferRoomComments?: (value: { [offerRoomId: string]: string }) => void
    discount: Nullable<number>
    manualPrice: Nullable<number>
    hideUnitPrices: boolean
    setHideUnitPrices?: (hide: boolean) => void
    hasChanges?: boolean
    phone?: string
    contactInfo: Nullable<ContactInfo>
    setContactInfo?: (contactInfo: EditableContactInfo) => void
}

export function OfferSummarySites({ discount, manualPrice, offerRooms, offerRoomComments, offerRoomNames, setOfferRoomComments, path, hideUnitPrices, setHideUnitPrices, hasChanges, phone, contactInfo, setContactInfo, offerId }: Props) {
    const history = useHistory();

    const validOffers = Object.values(offerRooms).filter(offerRoom => Object.keys(offerRoom.priceFields).length > 0);

    function Nav(title: string, children: JSX.Element) {
        return (
            <PageNavigation
                title={title}
                hasUnsavedChanges={hasChanges}
                safeUnsavedNavigation={[path]}
                rightAppBar={setHideUnitPrices ? (
                    <IconButton color="inherit" aria-label="vis/gjem enhetspriser" onClick={() => setHideUnitPrices(!hideUnitPrices)}>
                        {hideUnitPrices ? <MoneyOff /> : <AttachMoney />}
                    </IconButton>
                ) : undefined}
                backButton={
                    <IconButton onClick={() => history.goBack()} color="inherit">
                        <ArrowBack />
                    </IconButton>
                }
                bottomNavigation={
                    setOfferRoomComments ? offerNavigation(path.slice(0, path.lastIndexOf('/'))) : undefined
                }>
                {children}
            </PageNavigation>
        )
    }

    return (
        <RouterSwitch>
            <Route path={path + "/enkel-oppsummering"}>
                {Nav('Enkel', <BasicSummaryTable
                    hideUnitPrices={hideUnitPrices}
                    discount={discount}
                    manualPrice={manualPrice}
                    offerRooms={validOffers}
                />)}
            </Route>
            <Route path={path + "/detaljert-oppsummering"}>
                {Nav('Detaljert', <DetailedSummaryTable
                    hideUnitPrices={hideUnitPrices}
                    discount={discount}
                    manualPrice={manualPrice}
                    offerRooms={validOffers}
                    offerRoomNames={offerRoomNames}
                    offerRoomComments={offerRoomComments}
                    setOfferRoomComments={setOfferRoomComments}
                />)}
            </Route>
            <Route path={path + "/internt-regnskap"}>
                {Nav('Detaljert', <InternalOfferAccounting
                    offerRooms={validOffers}
                    discount={discount}
                    manualPrice={manualPrice}
                />)}
            </Route>
            <Route path={path + "/kontaktinfo"}>
                {Nav('Kontaktinfo', <OfferContactInfo
                    offerId={offerId}
                    phone={phone}
                    contactInfo={contactInfo}
                    setContactInfo={setContactInfo}
                />)}
            </Route>
        </RouterSwitch>
    )
}
